import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown, ChevronUp, Loader2, Search, ExternalLink, RotateCw, CreditCard,
  CheckCircle2, AlertTriangle, XCircle, Pencil, Save, X as XIcon,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const PLAN_LABEL = { free: "FREE", basic: "BASIC", pro: "PRO", ai_manager: "AI MANAGER™" };

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function truncId(id, head = 10) {
  if (!id) return "—";
  if (id.length <= head + 3) return id;
  return `${id.slice(0, head)}…`;
}

/** Single source of truth for the row badge — keeps colors and labels aligned. */
function HealthBadge({ health }) {
  const cfg = {
    complete: { label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    warning:  { label: "Attenzione", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
    missing:  { label: "ID mancanti", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
  }[health] || { label: "—", cls: "bg-neutral-50 text-neutral-700 border-neutral-200", Icon: AlertTriangle };
  const { Icon } = cfg;
  return (
    <span
      data-testid={`stripe-diag-badge-${health}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.cls}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {cfg.label}
    </span>
  );
}

function MonoId({ value }) {
  if (!value) {
    return <span className="text-neutral-300 text-xs">—</span>;
  }
  return (
    <span
      title={value}
      className="font-mono text-[11px] text-neutral-700 cursor-help select-all"
    >
      {truncId(value)}
    </span>
  );
}

export default function StripeDiagnosticsPanel() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [resyncing, setResyncing] = useState("");
  const [config, setConfig] = useState(null);          // Stripe env diagnostics
  const [editingId, setEditingId] = useState("");      // user currently editing Stripe IDs manually
  const [editCid, setEditCid] = useState("");
  const [editSid, setEditSid] = useState("");
  const [savingBind, setSavingBind] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const [r, cfg] = await Promise.all([
        api.get("/admin/stripe-diagnostics"),
        api.get("/admin/stripe-config"),
      ]);
      setRows(r.data?.users || []);
      setConfig(cfg.data || null);
      setLoaded(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento diagnostica");
    } finally {
      setLoading(false);
    }
  };

  // Lazy load: hit the endpoint only when the panel is opened for the first time.
  useEffect(() => {
    if (open && !loaded && !loading) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 2-step purge for test accounts — explicit dry-run preview before destructive apply
  const [purging, setPurging] = useState(false);
  const purgeTestAccounts = async () => {
    setPurging(true);
    try {
      const dry = await api.post("/admin/purge-test-accounts", {});
      const matched = dry.data?.matched || 0;
      const users = dry.data?.users || [];
      if (matched === 0) {
        toast.success("Nessun account test da eliminare.");
        return;
      }
      const preview = users.slice(0, 8).map((u) => u.email).join("\n  • ");
      const more = matched > 8 ? `\n  …e altri ${matched - 8}` : "";
      const ok = window.confirm(
        `ATTENZIONE — eliminazione DEFINITIVA di ${matched} account di test su questo ambiente.\n\n`
        + `Domini: @bridal.it, @test.it, @br.it, @x.it\n\n`
        + `Anteprima:\n  • ${preview}${more}\n\n`
        + `Verranno cancellati anche tutti i loro dati associati (clienti, eventi, team, preventivi, transazioni, notifiche).\n\n`
        + `Account reali (corasaniti…, lecce…) NON saranno toccati.\n\n`
        + `Procedere?`,
      );
      if (!ok) {
        toast.info("Operazione annullata.");
        return;
      }
      const res = await api.post("/admin/purge-test-accounts", { apply: true });
      const deleted = res.data?.deleted_users || 0;
      const totals = res.data?.totals || {};
      const extras = Object.entries(totals)
        .filter(([k, v]) => v && k !== "users")
        .map(([k, v]) => `${k}=${v}`)
        .join(", ");
      toast.success(
        `Eliminati ${deleted} account${extras ? ` + (${extras})` : ""}.`,
        { duration: 7000 },
      );
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore pulizia account test");
    } finally {
      setPurging(false);
    }
  };

  const filtered = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (qLow && !(r.email || "").toLowerCase().includes(qLow)) return false;
      if (planFilter !== "all" && r.plan !== planFilter) return false;
      if (statusFilter !== "all" && r.billing_status !== statusFilter) return false;
      return true;
    });
  }, [rows, q, planFilter, statusFilter]);

  const counters = useMemo(() => {
    const total = rows.length;
    const complete = rows.filter((r) => r.health === "complete").length;
    const warning = rows.filter((r) => r.health === "warning").length;
    const missing = rows.filter((r) => r.health === "missing").length;
    return { total, complete, warning, missing };
  }, [rows]);

  const openInStripe = (row) => {
    if (!row.stripe_customer_id) {
      toast.error("Customer ID mancante — sincronizza prima");
      return;
    }
    window.open(
      `https://dashboard.stripe.com/customers/${encodeURIComponent(row.stripe_customer_id)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const resync = async (row) => {
    setResyncing(row.id);
    try {
      const r = await api.post(`/admin/users/${row.id}/stripe-resync`);
      const data = r.data || {};
      if (data.ok === false) {
        // Stripe API responded successfully but found nothing — most often
        // because the configured key is in `test` mode while the customer
        // exists only on `live` (or vice versa). Surface the env mode so
        // the operator knows what to fix.
        const mode = config?.stripe_mode || "?";
        toast.warning(
          `${data.message || "Nessun customer Stripe trovato"} — Stripe mode: ${mode}.`,
          { duration: 6000 },
        );
      } else {
        toast.success(
          data.stripe_subscription_id
            ? "Sincronizzato (customer + subscription)"
            : "Customer trovato (nessuna subscription attiva)",
        );
      }
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setResyncing("");
    }
  };

  const beginEdit = (row) => {
    setEditingId(row.id);
    setEditCid(row.stripe_customer_id || "");
    setEditSid(row.stripe_subscription_id || "");
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditCid("");
    setEditSid("");
  };

  const saveBind = async (row) => {
    const cid = editCid.trim();
    const sid = editSid.trim();
    if (!cid && !sid) {
      toast.error("Inserisci almeno un ID");
      return;
    }
    if (cid && !cid.startsWith("cus_")) {
      toast.error("Customer ID deve iniziare con 'cus_'");
      return;
    }
    if (sid && !sid.startsWith("sub_")) {
      toast.error("Subscription ID deve iniziare con 'sub_'");
      return;
    }
    setSavingBind(true);
    try {
      await api.post(`/admin/users/${row.id}/stripe-manual-bind`, {
        stripe_customer_id: cid,
        stripe_subscription_id: sid,
      });
      toast.success("Binding aggiornato manualmente");
      cancelEdit();
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio binding");
    } finally {
      setSavingBind(false);
    }
  };

  return (
    <section
      className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
      data-testid="admin-stripe-diagnostics-section"
    >
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-neutral-50/60 transition-colors"
        data-testid="stripe-diag-toggle"
      >
        <div className="flex items-center gap-3">
          <CreditCard className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <div className="text-left">
            <div className="font-serif-heading text-base">Diagnostica Stripe</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              Verifica binding utente ↔ Stripe (customer & subscription IDs, scadenze, stato)
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {loaded && (
            <div className="hidden sm:flex items-center gap-2 text-[11px] text-neutral-500 tabular-nums">
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" strokeWidth={2} />{counters.complete}</span>
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" strokeWidth={2} />{counters.warning}</span>
              <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="w-3 h-3" strokeWidth={2} />{counters.missing}</span>
              <span className="text-neutral-400">/ {counters.total}</span>
            </div>
          )}
          {open
            ? <ChevronUp className="w-4 h-4 text-neutral-400" strokeWidth={1.8} />
            : <ChevronDown className="w-4 h-4 text-neutral-400" strokeWidth={1.8} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border" data-testid="stripe-diag-panel">
          {/* Env mode banner — surfaces the Stripe key mode so the operator
              knows whether Re-sync will hit live or test customers. */}
          {config && (
            <div
              className={`px-5 py-2.5 text-[11px] flex items-center justify-between gap-3 border-b border-border ${
                config.stripe_mode === "live"
                  ? "bg-emerald-50/60 text-emerald-800"
                  : config.stripe_mode === "test"
                    ? "bg-amber-50/70 text-amber-900"
                    : "bg-rose-50/70 text-rose-800"
              }`}
              data-testid="stripe-diag-env-banner"
            >
              <div className="flex items-center gap-3 tabular-nums">
                <span className="font-medium uppercase tracking-wider">
                  Stripe API · {config.stripe_mode}
                </span>
                <span className="opacity-70">key {config.key_prefix || "—"}</span>
                <span className="opacity-70">webhook {config.webhook_secret_present ? "✓" : "✗"}</span>
                <span className="opacity-70">price map {config.price_map_count}</span>
              </div>
              <div className="opacity-70">{config.billing_email}</div>
            </div>
          )}

          {/* Toolbar */}
          <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-border bg-neutral-50/40">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" strokeWidth={2} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca email…"
                className="pl-9 h-8 rounded-full text-xs"
                data-testid="stripe-diag-search"
              />
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid="stripe-diag-plan-filter">
                <SelectValue placeholder="Piano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Piano: tutti</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="ai_manager">AI Manager™</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid="stripe-diag-status-filter">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Stato: tutti</SelectItem>
                <SelectItem value="active">Attivo</SelectItem>
                <SelectItem value="trialing">Trial</SelectItem>
                <SelectItem value="past_due">Scaduto</SelectItem>
                <SelectItem value="canceled">Cancellato</SelectItem>
                <SelectItem value="inactive">Inattivo</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-neutral-500 tabular-nums" data-testid="stripe-diag-counter">
                {filtered.length} / {rows.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full h-8 text-xs"
                onClick={fetchRows}
                disabled={loading}
                data-testid="stripe-diag-reload"
              >
                <RotateCw className={`w-3 h-3 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
                Ricarica
              </Button>
              <button
                type="button"
                onClick={purgeTestAccounts}
                disabled={purging}
                title="Elimina su QUESTO ambiente gli account con domini @bridal.it, @test.it, @br.it, @x.it (con anteprima)"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="stripe-diag-purge"
              >
                {purging
                  ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />
                  : <AlertTriangle className="w-3 h-3" strokeWidth={1.8} />}
                Pulisci account test
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading && !loaded ? (
              <div className="py-10 flex items-center justify-center text-neutral-400">
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-sm text-neutral-400" data-testid="stripe-diag-empty">
                Nessun utente con piano a pagamento.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="stripe-diag-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 bg-neutral-50/60">
                    <th className="text-left font-medium px-4 py-2.5">Email</th>
                    <th className="text-left font-medium px-3 py-2.5">Piano</th>
                    <th className="text-left font-medium px-3 py-2.5">Stato</th>
                    <th className="text-left font-medium px-3 py-2.5">Customer ID</th>
                    <th className="text-left font-medium px-3 py-2.5">Subscription ID</th>
                    <th className="text-left font-medium px-3 py-2.5">Scadenza</th>
                    <th className="text-right font-medium px-4 py-2.5">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-neutral-50/50" data-testid={`stripe-diag-row-${r.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <HealthBadge health={r.health} />
                          <span className="text-xs text-neutral-800 truncate max-w-[200px]" title={r.email}>{r.email}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[11px] font-medium tracking-wider text-neutral-700">
                          {PLAN_LABEL[r.plan] || r.plan.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[11px] text-neutral-600 capitalize">
                          {r.billing_status}
                          {r.cancel_at_period_end ? <span className="ml-1 text-amber-600">· cancel</span> : null}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {editingId === r.id ? (
                          <Input
                            value={editCid}
                            onChange={(e) => setEditCid(e.target.value.trim())}
                            placeholder="cus_…"
                            className="h-6 text-[11px] font-mono rounded-sm px-2"
                            data-testid={`stripe-diag-edit-cid-${r.id}`}
                          />
                        ) : (
                          <MonoId value={r.stripe_customer_id} />
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {editingId === r.id ? (
                          <Input
                            value={editSid}
                            onChange={(e) => setEditSid(e.target.value.trim())}
                            placeholder="sub_…"
                            className="h-6 text-[11px] font-mono rounded-sm px-2"
                            data-testid={`stripe-diag-edit-sid-${r.id}`}
                          />
                        ) : (
                          <MonoId value={r.stripe_subscription_id} />
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-neutral-700 tabular-nums">
                        {formatDate(r.current_period_end)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {editingId === r.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveBind(r)}
                                disabled={savingBind}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-[11px] text-emerald-800 disabled:opacity-50"
                                data-testid={`stripe-diag-save-${r.id}`}
                              >
                                {savingBind
                                  ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />
                                  : <Save className="w-3 h-3" strokeWidth={1.8} />}
                                Salva
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-neutral-200 hover:bg-neutral-100 text-[11px] text-neutral-700"
                                data-testid={`stripe-diag-cancel-${r.id}`}
                              >
                                <XIcon className="w-3 h-3" strokeWidth={1.8} />
                                Annulla
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => openInStripe(r)}
                                disabled={!r.stripe_customer_id}
                                title={r.stripe_customer_id ? "Apri customer su Stripe" : "Customer ID mancante"}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed text-[11px] text-neutral-700"
                                data-testid={`stripe-diag-open-${r.id}`}
                              >
                                <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
                                Stripe
                              </button>
                              <button
                                type="button"
                                onClick={() => resync(r)}
                                disabled={resyncing === r.id}
                                title="Ricerca su Stripe e sincronizza ID + scadenza"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40 text-[11px] text-neutral-700"
                                data-testid={`stripe-diag-resync-${r.id}`}
                              >
                                {resyncing === r.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />
                                  : <RotateCw className="w-3 h-3" strokeWidth={1.8} />}
                                Re-sync
                              </button>
                              <button
                                type="button"
                                onClick={() => beginEdit(r)}
                                title="Inserisci manualmente Customer ID e/o Subscription ID (copia da Stripe Dashboard)"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-neutral-200 hover:bg-neutral-100 text-[11px] text-neutral-700"
                                data-testid={`stripe-diag-edit-${r.id}`}
                              >
                                <Pencil className="w-3 h-3" strokeWidth={1.8} />
                                Manuale
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
